import { useThree } from '@react-three/fiber'
import { useEffect } from 'react'
import * as THREE from 'three'

export function ThumbnailGenerator({ waypoints, setThumbnails })
{
    const { scene, gl } = useThree()

    useEffect(() =>
    {
        if (!waypoints || waypoints.length === 0) return

        let isMounted = true

        const generate = async () =>
        {
            // Give the scene a moment to fully render/texture load
            // In a real app we might want to wait for a 'ready' signal or user interaction
            await new Promise(r => setTimeout(r, 500))
            if (!isMounted) return

            const generated = {}
            const width = 320 // decent quality thumbnail
            const height = 180 // 16:9 aspect ratio

            const renderTarget = new THREE.WebGLRenderTarget(width, height)
            const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000)

            // Re-use buffers
            const buffer = new Uint8Array(width * height * 4)
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')
            const imgData = ctx.createImageData(width, height)

            for (let i = 0; i < waypoints.length; i++)
            {
                if (!isMounted) break
                const wp = waypoints[ i ]
                // Support both object structure with camera.anchor or flattened
                const anchor = wp.camera?.anchor?.position || wp.position
                const target = wp.camera?.targetPosition || wp.target

                if (anchor && target)
                {
                    // Position camera
                    camera.position.set(anchor.x, anchor.y, anchor.z)
                    camera.lookAt(target.x, target.y, target.z)
                    camera.updateMatrixWorld()

                    // Render
                    const originalTarget = gl.getRenderTarget()
                    gl.setRenderTarget(renderTarget)
                    gl.render(scene, camera)
                    gl.readRenderTargetPixels(renderTarget, 0, 0, width, height, buffer)
                    gl.setRenderTarget(originalTarget)

                    // Flip Y and put to canvas
                    for (let y = 0; y < height; y++)
                    {
                        for (let x = 0; x < width; x++)
                        {
                            const srcIdx = ((height - 1 - y) * width + x) * 4
                            const dstIdx = (y * width + x) * 4
                            imgData.data[ dstIdx ] = buffer[ srcIdx ]
                            imgData.data[ dstIdx + 1 ] = buffer[ srcIdx + 1 ]
                            imgData.data[ dstIdx + 2 ] = buffer[ srcIdx + 2 ]
                            imgData.data[ dstIdx + 3 ] = buffer[ srcIdx + 3 ]
                        }
                    }
                    ctx.putImageData(imgData, 0, 0)

                    // Create Blob URL
                    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.8))
                    if (blob)
                    {
                        generated[ i ] = URL.createObjectURL(blob)
                    }
                }
            }

            if (isMounted)
            {
                setThumbnails(generated)
            }

            // Cleanup
            renderTarget.dispose()
        }

        generate()

        return () => { isMounted = false }
    }, [ waypoints, scene, gl, setThumbnails ])

    return null
}
